import React, { Component, ErrorInfo, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { captureError } from '../lib/sentry';

interface Props extends WithTranslation {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  copied: boolean;
}

function generateErrorId(): string {
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

class ErrorBoundaryInner extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorInfo: null,
      errorId: generateErrorId(),
      copied: false,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Report to Sentry (no-op if DSN not configured)
    captureError(error, {
      componentStack: errorInfo.componentStack ?? undefined,
      errorId: this.state.errorId,
    });
    this.setState({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      copied: false,
    });
  };

  handleCopy = () => {
    const { error, errorInfo, errorId } = this.state;
    const text = [
      `Error ID: ${errorId}`,
      `Message: ${error?.message ?? 'Unknown'}`,
      `Stack: ${error?.stack ?? ''}`,
      `Component Stack: ${errorInfo?.componentStack ?? ''}`,
    ].join('\n\n');
    navigator.clipboard.writeText(text).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  render() {
    const { t } = this.props;
    const { hasError, error, errorInfo, errorId, copied } = this.state;

    if (hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Red top accent */}
            <div className="h-2 bg-gradient-to-r from-red-500 to-red-400" />

            <div className="p-8">
              <div className="text-center">
                {/* Icon */}
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>

                <h1 className="text-2xl font-bold text-gray-900">
                  {t('errors.somethingWrong', 'Oops! Something went wrong')}
                </h1>
                <p className="mt-2 text-gray-500 text-sm">
                  {t('errors.unexpectedError', 'An unexpected error occurred. Please try refreshing the page.')}
                </p>

                {/* Error ID badge */}
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
                  <span className="text-xs text-gray-500">{t('errors.errorId', 'Error ID')}:</span>
                  <span className="text-xs font-mono font-semibold text-gray-800">#{errorId}</span>
                </div>
              </div>

              {/* Dev details */}
              {process.env.NODE_ENV === 'development' && error && (
                <div className="mt-6">
                  <details className="bg-gray-50 rounded-xl p-4 text-left border border-gray-200">
                    <summary className="cursor-pointer text-sm font-semibold text-gray-700 select-none">
                      Error Details (dev only)
                    </summary>
                    <div className="mt-3 text-xs text-red-600 font-mono whitespace-pre-wrap overflow-auto max-h-48">
                      <strong>Error:</strong> {error.toString()}
                      {errorInfo && (
                        <>
                          {`\n\n`}<strong>Component Stack:</strong>{errorInfo.componentStack}
                        </>
                      )}
                    </div>
                  </details>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={this.handleReset}
                  className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors text-sm"
                >
                  {t('errors.tryAgain', 'Try Again')}
                </button>
                <button
                  onClick={() => { window.location.href = '/'; }}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-800 rounded-xl font-medium hover:bg-gray-200 transition-colors text-sm"
                >
                  {t('errors.goHome', 'Go Home')}
                </button>
                <button
                  onClick={this.handleCopy}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-colors text-sm"
                >
                  {copied ? '✅ Copied!' : t('errors.copyDetails', 'Copy Details')}
                </button>
              </div>

              <p className="mt-4 text-center text-xs text-gray-400">
                {t('errors.reportHint', 'Quote error ID #{errorId} when reporting this issue.').replace('{errorId}', errorId)}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const ErrorBoundary = withTranslation()(ErrorBoundaryInner);
export default ErrorBoundary;
